// T-286 (Food Cost): motore PURO di calcolo del costo ricetta.
//
// Costo ricetta = Σ(ingrediente: qty × costo unitario alimento)
//               + Σ(semilavorato: qty × costo del sub normalizzato sulla sua resa)
//
// Modello di costo v1 = ULTIMO PREZZO d'acquisto per alimento (T-286-a), iniettato
// come `foodUnitCost` (il resolver legge lo storico stock_in T-284 / fiscal_archive
// T-299). Il motore è PURO: nessun accesso a DB, tutto testabile.
//
// Principi (mustFix REVISE T-286):
//  - Ricorsione semilavorati via `parent_recipe_ids` (SEMPRE sincronizzato); la
//    qty/uom del sub si prende da `parent_recipes`. Se un figlio raggiungibile non
//    ha qty in parent_recipes (ricetta legacy) → `unavailable` (mai troncare a 'ok').
//  - Gate dimensionale sul bordo: la UdM con cui uso il sub deve combaciare con il
//    suo `yield_uom`; la UdM dell'ingrediente con la UdM del costo del food.
//    Niente conversione silenziosa in v1 → mismatch = `unavailable` (mai falso numero).
//  - MAI un costo 0 "falso": senza input di costo → `unavailable`.

import type { Recipe } from './recipe.js';
import { convertQty } from './uomConvert.js';

export type RecipeCostUnavailableReason =
  | 'no_cost_inputs'
  | 'ingredient_qty_missing'
  | 'ingredient_cost_unknown'
  | 'ingredient_uom_mismatch'
  | 'sub_recipe_not_found'
  | 'sub_recipe_qty_unknown'
  | 'sub_recipe_uom_mismatch'
  | 'sub_recipe_cycle'
  | 'sub_recipe_unavailable';

export interface RecipeCostOk {
  status: 'ok';
  /** Costo totale per produrre `yield_qty` della ricetta (valuta del costo unitario). */
  cost: number;
  /** Costo per porzione, se `yield_portions` è impostato; altrimenti null. */
  costPerPortion: number | null;
}
export interface RecipeCostUnavailable {
  status: 'unavailable';
  reason: RecipeCostUnavailableReason;
  /** id ricetta/food che ha causato l'indisponibilità (diagnostica UI). */
  at?: string;
}
export type RecipeCostResult = RecipeCostOk | RecipeCostUnavailable;

/**
 * Diagnosi per la UI (T-301): a differenza di `computeRecipeCost` (fail-fast,
 * niente numero se manca anche un solo costo), valuta TUTTI gli ingredienti/
 * semilavorati diretti e ritorna un costo PARZIALE + un semaforo, così una
 * ricetta incompleta mostra comunque quanto si sa, evidenziando cosa manca.
 */
export interface RecipeCostDiagnosis {
  total: number;       // ingredienti + semilavorati diretti
  resolved: number;    // quanti hanno un costo valido
  partialCost: number; // somma dei soli risolti (= costo pieno se resolved===total)
  costPerPortion: number | null; // partialCost / yield_portions (se impostato)
  severity: 'green' | 'orange' | 'red'; // verde=tutti, arancio=parziale, rosso=nessuno
  missing: Array<{ reason: RecipeCostUnavailableReason; at: string }>;
  // T-442 (Food Cost): valorizzazione riga-per-riga — così l'utente vede COME si
  // compone il costo (ingrediente × qty × costo unitario = € contribuito), non
  // solo il totale. Stesso ordine di valutazione: prima gli ingredienti diretti,
  // poi i semilavorati. `lineCost` è null (mai 0 falso) quando la riga non è
  // risolta; `reason` dice perché.
  lines: RecipeCostLine[];
}

export interface RecipeCostLine {
  kind: 'ingredient' | 'sub';
  /** food_id (ingrediente) o recipe_id (semilavorato). La UI risolve il nome. */
  at: string;
  /** Quantità in ricetta (per il sub: la qty in `parent_recipes`). Null se mancante. */
  qty: number | null;
  /** UdM della riga ricetta (per il sub: la UdM con cui si usa il sub). */
  uom: string | null;
  /**
   * Costo unitario nella UdM della riga: per l'ingrediente = `foodUnitCost`;
   * per il semilavorato = costo del sub normalizzato sulla sua resa (€/yield_uom).
   * Null quando ignoto.
   */
  unitCost: number | null;
  /** qty × unitCost quando la riga è risolta; null altrimenti (mai 0 falso). */
  lineCost: number | null;
  /** true = concorre al costo parziale; false = manca qualcosa (vedi `reason`). */
  resolved: boolean;
  /** Perché la riga non è risolta (solo quando `resolved === false`). */
  reason?: RecipeCostUnavailableReason;
  /** T-450 F1: conversione applicata (es. 200 g → 0,2 kg) per la Scheda. Presente
   *  solo quando la UdM riga differiva da quella del costo ed è stata convertita. */
  converted?: { qty: number; uom: string };
}

export interface RecipeCostContext {
  /** Ricette per id (per risolvere i semilavorati). */
  recipesById: Map<string, Recipe>;
  /** Ultimo costo unitario noto per alimento (modello "ultimo prezzo"); null se ignoto. */
  foodUnitCost: (foodId: string) => number | null;
  /**
   * UdM in cui è espresso il costo unitario dell'alimento (per il gate dimensionale
   * ingrediente↔food). Ritorna undefined per saltare il gate (uom del food ignota).
   */
  foodUom?: (foodId: string) => string | null | undefined;
}

const MAX_DEPTH = 5;
const normUom = (u: string | null | undefined): string => (u ?? '').toLowerCase().trim();

export function computeRecipeCost(recipe: Recipe, ctx: RecipeCostContext): RecipeCostResult {
  return costInternal(recipe, ctx, new Set<string>(), 0);
}

/**
 * Diagnosi per-ricetta (UI): costo parziale + semaforo + lista di cosa manca.
 * Valuta i SOLI ingredienti/semilavorati diretti (no ricorsione profonda — per
 * i sub usa `computeRecipeCost` sul figlio, fail-fast: un sub incompleto conta
 * come "non risolto"). MAI un costo 0 falso: i non risolti non sommano nulla.
 */
export function diagnoseRecipeCost(recipe: Recipe, ctx: RecipeCostContext): RecipeCostDiagnosis {
  const missing: Array<{ reason: RecipeCostUnavailableReason; at: string }> = [];
  const lines: RecipeCostLine[] = [];
  let total = 0;
  let resolved = 0;
  let partialCost = 0;

  for (const ing of recipe.ingredients ?? []) {
    total++;
    const unit = ctx.foodUnitCost(ing.food_id);
    if (ing.qty == null || !Number.isFinite(ing.qty) || ing.qty < 0) {
      missing.push({ reason: 'ingredient_qty_missing', at: ing.food_id });
      lines.push({ kind: 'ingredient', at: ing.food_id, qty: null, uom: ing.uom, unitCost: unit, lineCost: null, resolved: false, reason: 'ingredient_qty_missing' });
      continue;
    }
    if (unit == null || !Number.isFinite(unit) || unit < 0) {
      missing.push({ reason: 'ingredient_cost_unknown', at: ing.food_id });
      lines.push({ kind: 'ingredient', at: ing.food_id, qty: ing.qty, uom: ing.uom, unitCost: null, lineCost: null, resolved: false, reason: 'ingredient_cost_unknown' });
      continue;
    }
    // Quantità effettiva usata per il costo, nella UdM del COSTO alimento. Se le
    // UdM differiscono (es. ricetta in g, costo in €/kg) prova a convertire; se la
    // conversione non è derivabile (dimensioni diverse, es. pz↔kg) → mismatch.
    let costQty = ing.qty;
    let converted: { qty: number; uom: string } | undefined;
    if (ctx.foodUom) {
      const fu = ctx.foodUom(ing.food_id);
      if (fu != null && normUom(fu) !== normUom(ing.uom)) {
        const cq = convertQty(ing.qty, ing.uom ?? '', fu);
        if (cq == null) {
          missing.push({ reason: 'ingredient_uom_mismatch', at: ing.food_id });
          lines.push({ kind: 'ingredient', at: ing.food_id, qty: ing.qty, uom: ing.uom, unitCost: unit, lineCost: null, resolved: false, reason: 'ingredient_uom_mismatch' });
          continue;
        }
        costQty = cq;
        converted = { qty: cq, uom: fu };
      }
    }
    resolved++;
    const lineCost = costQty * unit;
    partialCost += lineCost;
    lines.push({ kind: 'ingredient', at: ing.food_id, qty: ing.qty, uom: ing.uom, unitCost: unit, lineCost, resolved: true, ...(converted ? { converted } : {}) });
  }

  const linkById = new Map<string, { qty: number | null; uom: string | null }>();
  for (const pr of recipe.parent_recipes ?? []) linkById.set(pr.recipe_id, { qty: pr.qty, uom: pr.uom });
  for (const subId of recipe.parent_recipe_ids ?? []) {
    total++;
    const link = linkById.get(subId);
    const linkQty = link?.qty ?? null;
    const linkUom = link?.uom ?? null;
    const sub = ctx.recipesById.get(subId);
    if (!sub) {
      missing.push({ reason: 'sub_recipe_not_found', at: subId });
      lines.push({ kind: 'sub', at: subId, qty: linkQty, uom: linkUom, unitCost: null, lineCost: null, resolved: false, reason: 'sub_recipe_not_found' });
      continue;
    }
    if (!link || link.qty == null || !Number.isFinite(link.qty) || link.qty < 0) {
      missing.push({ reason: 'sub_recipe_qty_unknown', at: subId });
      lines.push({ kind: 'sub', at: subId, qty: linkQty, uom: linkUom, unitCost: null, lineCost: null, resolved: false, reason: 'sub_recipe_qty_unknown' });
      continue;
    }
    if (sub.yield_qty == null || !Number.isFinite(sub.yield_qty) || sub.yield_qty <= 0) {
      missing.push({ reason: 'sub_recipe_unavailable', at: subId });
      lines.push({ kind: 'sub', at: subId, qty: linkQty, uom: linkUom, unitCost: null, lineCost: null, resolved: false, reason: 'sub_recipe_unavailable' });
      continue;
    }
    // Converti la qty con cui si usa il sub nella UdM della sua resa (yield_uom):
    // se non è derivabile (dimensioni diverse) → mismatch come prima.
    let subQty = link.qty;
    let subConverted: { qty: number; uom: string } | undefined;
    if (normUom(link.uom) !== normUom(sub.yield_uom)) {
      const cq = convertQty(link.qty, link.uom ?? '', sub.yield_uom ?? '');
      if (cq == null) {
        missing.push({ reason: 'sub_recipe_uom_mismatch', at: subId });
        lines.push({ kind: 'sub', at: subId, qty: linkQty, uom: linkUom, unitCost: null, lineCost: null, resolved: false, reason: 'sub_recipe_uom_mismatch' });
        continue;
      }
      subQty = cq;
      subConverted = { qty: cq, uom: sub.yield_uom ?? '' };
    }
    const sc = computeRecipeCost(sub, ctx);
    if (sc.status !== 'ok') {
      missing.push({ reason: 'sub_recipe_unavailable', at: subId });
      lines.push({ kind: 'sub', at: subId, qty: linkQty, uom: linkUom, unitCost: null, lineCost: null, resolved: false, reason: 'sub_recipe_unavailable' });
      continue;
    }
    resolved++;
    const unitCost = sc.cost / sub.yield_qty; // €/yield_uom del sub
    const lineCost = subQty * unitCost;
    partialCost += lineCost;
    lines.push({ kind: 'sub', at: subId, qty: link.qty, uom: linkUom, unitCost, lineCost, resolved: true, ...(subConverted ? { converted: subConverted } : {}) });
  }

  const severity: RecipeCostDiagnosis['severity'] =
    total === 0 || resolved === 0 ? 'red' : resolved < total ? 'orange' : 'green';
  const costPerPortion =
    recipe.yield_portions && Number.isFinite(recipe.yield_portions) && recipe.yield_portions > 0 ? partialCost / recipe.yield_portions : null;
  return { total, resolved, partialCost, costPerPortion, severity, missing, lines };
}

function costInternal(
  recipe: Recipe,
  ctx: RecipeCostContext,
  seen: Set<string>,
  depth: number,
): RecipeCostResult {
  // Cicli: se la ricetta è già nel path corrente, o oltre la profondità massima.
  if (seen.has(recipe.id) || depth > MAX_DEPTH) {
    return { status: 'unavailable', reason: 'sub_recipe_cycle', at: recipe.id };
  }
  const nextSeen = new Set(seen).add(recipe.id);

  const ingredients = recipe.ingredients ?? [];
  const subIds = recipe.parent_recipe_ids ?? [];
  if (ingredients.length === 0 && subIds.length === 0) {
    return { status: 'unavailable', reason: 'no_cost_inputs', at: recipe.id };
  }

  let total = 0;

  // 1) Ingredienti diretti.
  for (const ing of ingredients) {
    if (ing.qty == null || !Number.isFinite(ing.qty) || ing.qty < 0) return { status: 'unavailable', reason: 'ingredient_qty_missing', at: ing.food_id };
    const unit = ctx.foodUnitCost(ing.food_id);
    if (unit == null || !Number.isFinite(unit) || unit < 0) return { status: 'unavailable', reason: 'ingredient_cost_unknown', at: ing.food_id };
    // Quantità nella UdM del COSTO alimento: converti kg↔g/l↔ml (come
    // diagnoseRecipeCost), mismatch solo se non derivabile (es. pz↔kg). Le due
    // funzioni DEVONO concordare: un sub in g con costo €/kg deve costare uguale
    // sia standalone (diagnose) sia annidato (questo motore, via computeRecipeCost).
    let costQty = ing.qty;
    if (ctx.foodUom) {
      const fu = ctx.foodUom(ing.food_id);
      if (fu != null && normUom(fu) !== normUom(ing.uom)) {
        const cq = convertQty(ing.qty, ing.uom ?? '', fu);
        if (cq == null) {
          return { status: 'unavailable', reason: 'ingredient_uom_mismatch', at: ing.food_id };
        }
        costQty = cq;
      }
    }
    total += costQty * unit;
  }

  // 2) Semilavorati — ricorsione via parent_recipe_ids, qty/uom da parent_recipes.
  const linkById = new Map<string, { qty: number | null; uom: string | null }>();
  for (const pr of recipe.parent_recipes ?? []) linkById.set(pr.recipe_id, { qty: pr.qty, uom: pr.uom });

  for (const subId of subIds) {
    const sub = ctx.recipesById.get(subId);
    if (!sub) return { status: 'unavailable', reason: 'sub_recipe_not_found', at: subId };
    const link = linkById.get(subId);
    if (!link || link.qty == null || !Number.isFinite(link.qty) || link.qty < 0) return { status: 'unavailable', reason: 'sub_recipe_qty_unknown', at: subId };
    if (sub.yield_qty == null || !Number.isFinite(sub.yield_qty) || sub.yield_qty <= 0) {
      return { status: 'unavailable', reason: 'sub_recipe_unavailable', at: subId };
    }
    // Converti la qty con cui si usa il sub nella UdM della sua resa (yield_uom),
    // coerente con diagnoseRecipeCost; mismatch solo se non derivabile.
    let subQty = link.qty;
    if (normUom(link.uom) !== normUom(sub.yield_uom)) {
      const cq = convertQty(link.qty, link.uom ?? '', sub.yield_uom ?? '');
      if (cq == null) {
        return { status: 'unavailable', reason: 'sub_recipe_uom_mismatch', at: subId };
      }
      subQty = cq;
    }
    const subResult = costInternal(sub, ctx, nextSeen, depth + 1);
    if (subResult.status !== 'ok') {
      // Propaga: un sub non calcolabile rende non calcolabile il padre (mai falso 'ok').
      return subResult.reason === 'sub_recipe_cycle'
        ? subResult
        : { status: 'unavailable', reason: 'sub_recipe_unavailable', at: subId };
    }
    total += subQty * (subResult.cost / sub.yield_qty);
  }

  const costPerPortion =
    recipe.yield_portions && Number.isFinite(recipe.yield_portions) && recipe.yield_portions > 0 ? total / recipe.yield_portions : null;
  return { status: 'ok', cost: total, costPerPortion };
}
